import { forwardRef } from "react";
import { clsx } from "@/utils";
import { Icon, Typography } from "..";
import { baseClass, getVariantClass, iconBaseClass } from "./themes";
import { ButtonProps } from "./types";

export const Button = forwardRef<HTMLButtonElement, ButtonProps>((buttonProps, ref) => {
  const {
    variant = "primary",
    size = "md",
    className,
    type = "button",
    startIcon,
    endIcon,
    isCustomIcon = false,
    iconClassName,
    children,
    iconVariant,
    isDropdown,
    onDropdownClick = (e) => props?.onClick?.(e),
    disabled,
    ...props
  } = buttonProps;

  const variantClass = getVariantClass(variant);

  const sizeClass = {
    "px-3 py-2 h-[28px]": size === "xs",
    "px-3 py-2 h-[32px]": size === "sm",
    "px-3 sm:px-5 py-1.5 sm:py-2.5 h-[32px] sm:h-[40px]": size === "md",
    "px-6 py-3.5 h-[44px]": size === "lg",
    "px-7 py-4 h-[56px]": size === "xl",
  };

  const renderStartIcon = () => {
    if (!startIcon) return null;

    if (typeof startIcon === "string") {
      return (
        <Icon
          name={startIcon}
          size="sm"
          variant={iconVariant}
          className={clsx(iconBaseClass, iconClassName)}
          isCustom={isCustomIcon}
          aria-hidden="true"
        />
      );
    }

    return <startIcon.type {...startIcon.props} />;
  };

  const renderEndIcon = () => {
    if (!endIcon) return null;

    if (typeof endIcon === "string") {
      return (
        <Icon
          name={endIcon}
          size="sm"
          variant={iconVariant}
          className={clsx(iconBaseClass, iconClassName)}
          isCustom={isCustomIcon}
          aria-hidden="true"
        />
      );
    }

    return <endIcon.type {...endIcon.props} />;
  };

  const renderDropdownButton = () => {
    if (isDropdown) {
      const dropdownVariantClass = clsx({
        "bg-primary hover:bg-primary-dark border-l border-white": !disabled,
        "bg-neutral-gray hover:bg-neutral-gray border-l border-neutral-mid-gray": disabled,
      });

      return (
        <button
          type="button"
          onClick={onDropdownClick}
          disabled={disabled}
          className={clsx("flex items-center justify-center rounded-r-md transition-colors", sizeClass, dropdownVariantClass, {
            "!px-1.5": size === "xs" || size === "sm",
            "!px-2.5": size === "md",
            "!px-3": size === "lg" || size === "xl",
          })}
        >
          <Icon
            name="arrow-down"
            isCustom
            size="sm"
            className={clsx({
              "text-white": !disabled,
              "text-neutral-dark-gray": disabled,
            })}
          />
        </button>
      );
    }
  };

  if (isDropdown) {
    return (
      <div className={clsx("flex", className)}>
        <button
          {...props}
          type={type}
          className={clsx(baseClass, getVariantClass(disabled ? "custom" : "primary"), sizeClass, "rounded-r-none", {
            "!bg-neutral-gray !text-neutral-silver-gray": disabled,
          })}
          disabled={disabled}
          ref={ref}
        >
          {renderStartIcon()}
          <Typography variant="action" className="leading-5">
            {children}
          </Typography>
          {renderEndIcon()}
        </button>
        {renderDropdownButton()}
      </div>
    );
  }

  return (
    <button {...props} type={type} className={clsx(baseClass, variantClass, sizeClass, className)} disabled={disabled} ref={ref}>
      {renderStartIcon()}
      <Typography variant="action" className="leading-5">
        {children}
      </Typography>
      {renderEndIcon()}
    </button>
  );
});

Button.displayName = "Button";
